import { Link } from "react-router-dom";
import { ArrowRight, Receipt, Calculator, FileCheck2, PiggyBank, Sparkles } from "lucide-react";

const SERVICES = [
  { icon: PiggyBank, title: "Retirement Planning", blurb: "Roth conversions, Required Minimum Distributions, Social Security benefit taxation, and the tax mechanics of qualified-plan distributions." },
  { icon: Receipt, title: "Individual Tax Preparation", blurb: "Federal and multi-state return preparation for W-2 earners, self-employed professionals, and families." },
  { icon: Calculator, title: "Small-Business Tax Planning", blurb: "Year-round tax planning for sole proprietors, LLCs, and S-corps — structured around cash flow, not just April." },
  { icon: FileCheck2, title: "IRS Representation", blurb: "As a federally licensed Enrolled Agent, I represent clients before all administrative levels of the IRS — audits, appeals, and collections." },
];

const PILLARS = [
  { value: "Retirement", label: "Roth conversions, RMDs, Social Security taxation, and the tax mechanics of qualified-plan distributions." },
  { value: "Tax", label: "Year-round preparation, planning, and IRS representation under federal Enrolled Agent licensure." },
  { value: "Personal", label: "Every engagement is led by Harold directly. No junior preparers — ever." },
];

export default function HomePage() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#F9F8F6]">
        <div className="absolute inset-0 grain pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-24 md:pb-32 relative">
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-7 fade-in">
              <div className="text-xs uppercase tracking-[0.3em] text-[#8E6A2B] mb-8" data-testid="hero-overline">
                — Financial Planning · Rockville, Maryland
              </div>
              <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[1.05] text-[#0F292F]">
                <span className="italic text-[#8E6A2B]">Retirement</span> planning.<br />
                Tax planning.<br />
                Done carefully.
              </h1>
              <p className="mt-10 text-lg md:text-xl text-[#52525B] max-w-xl leading-relaxed">
                H. Elliot Associates is a <strong className="text-[#0F292F]">financial
                planning firm</strong> specializing in <strong className="text-[#0F292F]">
                retirement planning</strong> and <strong className="text-[#0F292F]">tax
                planning</strong> for federal employees, individuals, and small-business
                owners across Maryland, DC, and Virginia.
              </p>
              <div className="mt-12 flex flex-wrap gap-5">
                <Link to="/book" data-testid="hero-book-btn" className="btn-primary">
                  Book a Consultation <ArrowRight size={16} />
                </Link>
                <Link to="/services" data-testid="hero-services-btn" className="btn-secondary">
                  What we do today
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="aspect-[4/5] w-full overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/14526255/pexels-photo-14526255.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720"
                  alt="Modern office architecture"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-[#0F292F] text-white p-6 max-w-[280px] hidden md:block">
                <div className="text-xs uppercase tracking-[0.25em] text-[#CFA968] mb-2">Two Specialties</div>
                <p className="text-sm leading-relaxed">
                  Year-round retirement planning and tax planning, with deep specialization
                  in the unique benefits of federal employees and retirees.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-24 grid grid-cols-1 md:grid-cols-3 border-t border-black/10 pt-12 stagger">
            {PILLARS.map((s) => (
              <div key={s.label} className="md:border-r md:last:border-r-0 border-black/10 md:pr-12 mb-6 md:mb-0">
                <div className="font-serif text-3xl md:text-4xl text-[#0F292F] font-light">{s.value}</div>
                <div className="text-sm text-[#52525B] mt-3 max-w-[280px] leading-relaxed">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* APPROACH */}
      <section className="relative bg-[#F3F1EC] py-24 md:py-32">
        <div className="section-watermark left-0 top-8">approach</div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <div className="grid lg:grid-cols-12 gap-16">
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.3em] text-[#8E6A2B] mb-6">Our Approach</div>
              <h2 className="font-serif text-4xl md:text-5xl text-[#0F292F] font-light leading-tight">
                Retirement and tax, planned together — year round.
              </h2>
            </div>
            <div className="lg:col-span-7 space-y-8 text-[#1A1A1A]">
              <p className="text-lg leading-relaxed">
                Retirement is the longest financial decision most people will ever make,
                and most of its outcomes are determined years before retirement begins.
                We work with clients all twelve months on the choices that compound — modeling
                Roth conversions, mapping Required Minimum Distributions, and analyzing
                the tax treatment of Social Security and qualified-plan distributions
                <em> before</em> decisions are made, not after.
              </p>
              <p className="text-lg leading-relaxed">
                Year-round tax planning sits alongside the retirement work, under our
                principal's federal Enrolled Agent licensure — covering preparation,
                strategy, and IRS representation. Retirement engagements focus on planning
                and the tax mechanics of retirement decisions; they do not include the
                recommendation, selection, or management of securities.
              </p>
              <Link to="/services" className="inline-flex items-center gap-3 text-sm tracking-wide font-semibold text-[#0F292F] he-link">
                See the full list of services <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-16">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-[#8E6A2B] mb-5">What We Do Today</div>
              <h2 className="font-serif text-4xl md:text-5xl text-[#0F292F] font-light tracking-tight max-w-2xl">
                Two specialties. Four services.
              </h2>
            </div>
            <Link to="/services" data-testid="services-cta" className="btn-secondary">All services</Link>
          </div>

          <div className="grid md:grid-cols-2 gap-px bg-black/10 border border-black/10">
            {SERVICES.map((s) => (
              <div
                key={s.title}
                data-testid={`service-card-${s.title.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
                className="bg-white p-10 md:p-12 hover:bg-[#F9F8F6] transition-colors duration-500 group"
              >
                <s.icon size={32} strokeWidth={1.2} className="text-[#8E6A2B] mb-8" />
                <h3 className="font-serif text-2xl md:text-3xl text-[#0F292F] mb-4 leading-tight">{s.title}</h3>
                <p className="text-[#52525B] leading-relaxed mb-8">{s.blurb}</p>
                <div className="text-xs uppercase tracking-[0.2em] text-[#0F292F] inline-flex items-center gap-2 group-hover:text-[#8E6A2B]">
                  Learn more <ArrowRight size={12} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEDERAL EMPLOYEES SPECIALTY */}
      <section className="bg-[#F3F1EC] py-24 md:py-32 border-t border-black/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="text-xs uppercase tracking-[0.3em] text-[#8E6A2B] mb-6">— Our Niche</div>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#0F292F] font-light tracking-tight leading-[1.05] mb-8">
                Built for <span className="italic text-[#8E6A2B]">federal employees</span><br />
                and recent retirees.
              </h2>
              <p className="text-lg text-[#1A1A1A] leading-relaxed max-w-2xl mb-6">
                TSP withdrawals. FERS and CSRS pensions. The Special Retirement Supplement.
                Social Security with WEP and GPO offsets. State-of-residence questions
                for Foreign Service Officers and transferring employees.
              </p>
              <p className="text-lg text-[#1A1A1A] leading-relaxed max-w-2xl mb-6">
                These are the tax and retirement questions that confuse most CPAs — and the
                daily reality of every federal worker approaching retirement. Our founder
                spent twenty years inside the federal compensation system before starting
                this firm. We don't read about it. We lived it.
              </p>
              <p className="text-base text-[#52525B] italic leading-relaxed max-w-2xl mb-10">
                Through every season of public service — steady or uncertain — careful
                planning is what keeps a federal career on course.
              </p>
              <Link to="/federal-employees" data-testid="home-fed-cta" className="btn-primary">
                Explore the Federal-Employee Practice <ArrowRight size={16} />
              </Link>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-white border border-black/10 p-8 md:p-10">
                <div className="text-xs uppercase tracking-[0.25em] text-[#8E6A2B] mb-5">Topics We Know Cold</div>
                <ul className="space-y-3 text-sm text-[#1A1A1A]">
                  <li className="flex items-start gap-3"><span className="w-5 h-px bg-[#8E6A2B] mt-3 flex-shrink-0" />TSP withdrawal tax strategy (Traditional & Roth)</li>
                  <li className="flex items-start gap-3"><span className="w-5 h-px bg-[#8E6A2B] mt-3 flex-shrink-0" />FERS & CSRS pension taxation</li>
                  <li className="flex items-start gap-3"><span className="w-5 h-px bg-[#8E6A2B] mt-3 flex-shrink-0" />WEP / GPO offsets on Social Security</li>
                  <li className="flex items-start gap-3"><span className="w-5 h-px bg-[#8E6A2B] mt-3 flex-shrink-0" />Special Retirement Supplement (SRS)</li>
                  <li className="flex items-start gap-3"><span className="w-5 h-px bg-[#8E6A2B] mt-3 flex-shrink-0" />Foreign Service & overseas-posting tax issues</li>
                  <li className="flex items-start gap-3"><span className="w-5 h-px bg-[#8E6A2B] mt-3 flex-shrink-0" />Military service-credit (buyback) tax mechanics</li>
                  <li className="flex items-start gap-3"><span className="w-5 h-px bg-[#8E6A2B] mt-3 flex-shrink-0" />State-of-residence planning for transferring feds</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOUNDER / ABOUT */}
      <section className="relative py-24 md:py-32 bg-[#0F292F] text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-4 order-2 lg:order-1">
              <div className="aspect-square w-full max-w-[200px] overflow-hidden ring-1 ring-white/15">
                <img
                  src="/images/harold-hodges.jpg"
                  alt="Harold Hodges, MBA · EA · PMP — founder of H. Elliot Associates"
                  className="w-full h-full object-cover scale-110"
                  style={{ filter: "grayscale(60%) contrast(1.05) brightness(0.95)" }}
                />
              </div>
              <div className="mt-5 text-[10px] uppercase tracking-[0.25em] text-[#CFA968]">Harold Hodges</div>
              <div className="text-xs text-white/60 mt-1">MBA · EA · PMP · Founder</div>
            </div>
            <div className="lg:col-span-8 order-1 lg:order-2">
              <div className="text-xs uppercase tracking-[0.3em] text-[#CFA968] mb-6">— The Founder</div>
              <h2 className="font-serif text-4xl md:text-5xl font-light leading-tight mb-8">
                Credentialed where it counts — <span className="italic text-[#CFA968]">honest</span> about what's pending.
              </h2>
              <p className="text-lg text-white/70 leading-relaxed max-w-xl mb-6">
                Harold Hodges, MBA · EA · PMP, brings more than three decades of experience
                in the federal government and nonprofit sector — including twenty years
                with the U.S. Department of State — to the practice of tax. He
                founded H. Elliot Associates in 2012 to deliver year-round tax and retirement
                planning with the clarity typically reserved for the largest clients of high-fee
                firms.
              </p>
              <p className="text-lg text-white/70 leading-relaxed max-w-xl mb-10">
                He is actively pursuing additional credentials to broaden the firm's
                services in the future. The status of those credentials is tracked publicly
                on the Team page.
              </p>
              <Link to="/team" className="btn-accent">
                Read Harold's full profile <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* GUIDING PRINCIPLE */}
      <section className="py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <Sparkles size={28} strokeWidth={1.2} className="text-[#8E6A2B] mx-auto mb-8" />
          <blockquote className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#0F292F] font-light leading-tight italic">
            "A good practice returns more than a refund — it returns clarity. Whether the
            question is this April's return or the next thirty years of retirement, every
            decision should be one you understand."
          </blockquote>
          <div className="mt-10 text-sm tracking-wide text-[#52525B]">
            <span className="font-semibold text-[#0F292F]">Harold Hodges, EA</span> · Founder
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-[#F3F1EC] py-20 md:py-24 border-t border-black/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <h2 className="font-serif text-4xl md:text-5xl text-[#0F292F] font-light tracking-tight">
              Let's have a first conversation.
            </h2>
            <p className="mt-4 text-lg text-[#52525B] max-w-2xl">
              A 30-minute introductory consultation at no cost — bring a tax question, a
              retirement question, or both. We'll review your situation and explain how we
              can help, or refer you to someone who's a better fit.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/book" data-testid="cta-book-btn" className="btn-primary">
              Book a Consultation <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
